import clsx from 'clsx';
import type { ComponentProps } from 'react';
import { NavLink, useSearchParams } from 'react-router';
import FilterList from './FilterList';
import { useFilterState } from './FilterStateContext';

export function FilterMenu() {
  return (
    <div className="mb-6 space-y-3">
      <Level1Filters />
      <Level2Filters />
    </div>
  );
}

export function CurrentFilterTitle() {
  const { level0Label, level1Items, level2Items } = useFilterState();

  const currentLevel2 = level2Items.find((item) => {
    return !item.isRoot && item.isCurrent;
  })?.label;

  if (currentLevel2) return <>{currentLevel2}</>;

  const currentLevel1 = level1Items.find((item) => {
    return !item.isRoot && item.isCurrent;
  })?.label;

  if (currentLevel1) return <>{currentLevel1}</>;

  return <>{level0Label}</>;
}

function Level1Filters() {
  const { level1Items } = useFilterState();
  const [searchParams] = useSearchParams();

  if (!level1Items.length) return null;

  return (
    <nav>
      <FilterList className="gap-x-2 gap-y-1.25 md:gap-x-3">
        {level1Items.map((item, index) => {
          const isDeals =
            item.value === 'deals' || item.value.endsWith('> deals');

          return (
            <NavLink
              data-testid={`nav-${item.value}`}
              key={`level1-${index}-${item.value.toLowerCase()}`}
              to={{
                pathname: item.pathname,
                search: nextSearch(searchParams, item),
              }}
              className={clsx(
                'group font-medium text-nowrap',
                isDeals && 'text-sale',
              )}
            >
              {item.label}
              <div
                className={clsx(
                  'h-[1.5px] mt-[-3px]',
                  'transition-bg duration-150 ease-out',
                  isDeals ? 'group-hover:bg-sale' : 'group-hover:bg-grey-90',
                  item.isCurrent && (isDeals ? 'bg-sale' : 'bg-grey-90'),
                )}
              />
            </NavLink>
          );
        })}
      </FilterList>
    </nav>
  );
}

function Level2Filters() {
  const { level2Items } = useFilterState();
  const [searchParams] = useSearchParams();

  if (!level2Items.length) return null;

  return (
    <nav>
      <FilterList className="gap-x-1 gap-y-1.25">
        {level2Items.map((item) => {
          return (
            <NavLink
              key={item.value}
              data-testid={`filter-${item.value}`}
              to={{
                pathname: item.pathname,
                search: nextSearch(searchParams, item),
              }}
              className="grow-0 shrink-0 group"
            >
              <Pill
                highlight={item.isCurrent}
                dim={!item.isCurrent && item.count <= 0}
              >
                {item.label}
              </Pill>
            </NavLink>
          );
        })}
      </FilterList>
    </nav>
  );
}

function Pill({
  highlight,
  dim,
  className,
  ...props
}: ComponentProps<'div'> & {
  highlight?: boolean | undefined;
  dim?: boolean | undefined;
}) {
  return (
    <div
      {...props}
      className={clsx(
        'px-2 py-0.5 font-medium text-xs rounded-full transition-[color,background-color,opacity]',
        highlight
          ? 'bg-grey-90 text-white'
          : 'bg-grey-20 text-grey-90 group-hover:bg-grey-40 group-active:bg-grey-90 group-active:text-white',
        dim && 'opacity-50',
        className,
      )}
    />
  );
}

function nextSearch(search: URLSearchParams, item: { count: number }) {
  const params = new URLSearchParams(search);

  params.delete('page');

  if (item.count <= 0) params.delete('query');

  return `?${params.toString()}`;
}
